import { useEffect, useRef, type ReactNode } from 'react';
import { useActions, useSteve } from '../state/context';

/** Neuf barres, comme dans le design. */
export const VU_BARS = 9;

/** Hauteur au repos, en pourcentage du bac. */
const REST_HEIGHT = '8%';

interface Props {
  readonly className: string | undefined;
  readonly barClassName: string | undefined;
}

/**
 * VU-mètre du mixeur.
 *
 * Les barres sont mises à jour par écriture directe sur `style` dans une boucle
 * `requestAnimationFrame` : à 60 images par seconde, passer par l'état React
 * ferait re-rendre tout le skin soixante fois par seconde pour neuf hauteurs.
 * La boucle ne tourne que pendant la lecture.
 */
export function VuMeter({ className, barClassName }: Props): ReactNode {
  const { playing } = useSteve();
  const { readBars } = useActions();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const bars = Array.from(node.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (bars.length === 0) return;

    const rest = (): void => {
      for (const bar of bars) {
        bar.style.height = REST_HEIGHT;
        bar.style.opacity = '1';
      }
    };

    if (!playing) {
      rest();
      return;
    }

    const levels = new Float32Array(bars.length);
    let frame = 0;
    const draw = (): void => {
      if (readBars(levels)) {
        for (let index = 0; index < bars.length; index += 1) {
          const bar = bars[index];
          const level = levels[index] ?? 0;
          if (!bar) continue;
          bar.style.height = `${String(Math.max(8, level * 100))}%`;
          bar.style.opacity = String(0.45 + level * 0.55);
        }
      }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);

    return () => {
      window.cancelAnimationFrame(frame);
      rest();
    };
  }, [playing, readBars]);

  return (
    <div ref={ref} className={className} aria-hidden="true">
      {Array.from({ length: VU_BARS }, (_, index) => (
        <div key={index} className={barClassName} />
      ))}
    </div>
  );
}
